import type { ResultOf } from "@graphql-typed-document-node/core";
import { graphql } from "../gql";

export const query = graphql(`
    query SubmittedTask($id: ID!) {
        submittedTask(id:$id) {
            id
            status
            url
            name
            objectives
            inspiration {
                mediaType
                src
            }
            icon
            miles
            timeEstimate
            finishDatetime
            showSharedAnswers
            feedbacker {
                id
                firstName
                lastName
                profilePicSrc
            }
            media {
                id
                mediaType
                src
                name
                logo
            }
            task {
                id
            }
            submittedTaskVariant {
                id
                variantType
                variantTitle
                description
                eventStart
                eventEnd
                taskQuestions {
                    id
                    answerType
                    questionContent
                    quizId
                    pollId
                    surveyGizmoUrl
                    requireAllCheckmarks
                    showPollResults
                    checkboxOptions {
                        id
                        content
                    }
                }
                teamScan {
                    id
                    status
                    myLink
                }
                pulse {
                    id
                    question1
                    question2
                    link
                    pulseAnswers {
                        id
                        answer1
                        answer2
                    }
                }
                feedbackRequest {
                    id
                    url
                }
                taskAiThread {
                    id
                    aiThreadItems {
                        id
                        type
                        role
                        content
                    }
                }
            }
            learningPathItem {
                id
                type
                miles
                earnedMiles
                tasks {
                    id
                    locked
                    icon
                    doInGroup
                    name
                    miles
                    deadline
                    url
                    shareAnswers
                    taskVariants {
                        id
                        variantType
                    }
                    submittedTask {
                        id
                        url
                        status
                    }
                }
                pathInPath {
                    id
                    icon
                    url
                    showGroup
                }
                taskChoice {
                    id
                    icon
                    url
                    name
                    complete
                }
                learningPath {
                    id
                    open
                    pathGroup {
                        id
                        members {
                            id
                            person {
                                id
                                firstName
                                lastName
                                profilePicSrc
                            }
                        }
                    }
                }
            }
            collectFeedback {
                id
                anonymous
                collectFeedbackAnswerSets {
                    id
                    firstName
                    lastName
                    submitted
                    collectFeedbackAnswers {
                        id
                        nr
                        type
                        question
                        translation
                        leftLabel
                        rightLabel
                        leftTranslation
                        rightTranslation
                        textAnswer
                        likertAnswer
                    }
                }
            }
            thread {
                id
                type
                subtype
                contentFormat
                content
                scaleScore {
                    earnedScore
                    possibleScore
                }
                person {
                    id
                    firstName
                    lastName
                    profilePicSrc
                }
                taskAnswers {
                    id
                    answerType
                    questionContent
                    answerContent
                    checkboxAnswers {
                        id
                        content
                        checked
                    }
                    taskLikertAnswers {
                        id
                        statement
                        leftLabel
                        rightLabel
                        value
                    }
                    showPollResults
                    quizAnswerSet {
                        id
                        results
                        answers {
                            id
                            answer
                            answeredQuestion {
                                id
                                number
                                levelNumber
                                description
                                correctAnswer
                                explanation
                                quizOptions {
                                    id
                                    letter
                                    content
                                }
                            }
                        }
                    }
                    pollAnswerSet {
                        id
                        pollAnswerCount
                        answers {
                            id
                            answers
                            question {
                                id
                                number
                                content
                                chartType
                                options {
                                    id
                                    number
                                    content
                                    answerCount
                                }
                            }
                        }
                    }
                    taskAnswerFile {
                        id
                        logoSrc
                        src
                        name
                    }
                    impactTrackerAnswerSet {
                        id
                        impactLikertAnswers {
                            id
                            statement
                            leftLabel
                            rightLabel
                            value
                        }
                    }
                    selectInImageMedia {
                        id
                        mediaType
                        src
                        name
                        logo
                    }
                    imageSelectionAnswers {
                        id
                        part
                        clickX
                        clickY
                        answerType
                        answer
                        explanation
                    }
                    imageSelectionScore {
                        earnedScore
                        possibleScore
                    }
                }
            }
            sharedAnswers {
                id
                taskId
                type
                people {
                    id
                    firstName
                    lastName
                    profilePicSrc
                }
                taskGroups
                pathGroups
                read
                taskAnswers {
                    id
                    answerType
                    questionContent
                    answerContent
                    checkboxAnswers {
                        id
                        content
                        checked
                    }
                    taskLikertAnswers {
                        id
                        statement
                        leftLabel
                        rightLabel
                        value
                    }
                    showPollResults
                    quizAnswerSet {
                        id
                        results
                        answers {
                            id
                            answer
                            answeredQuestion {
                                id
                                number
                                levelNumber
                                description
                                correctAnswer
                                explanation
                                quizOptions {
                                    id
                                    letter
                                    content
                                }
                            }
                        }
                    }
                    pollAnswerSet {
                        id
                        pollAnswerCount
                        answers {
                            id
                            answers
                            question {
                                id
                                number
                                content
                                chartType
                                options {
                                    id
                                    number
                                    content
                                    answerCount
                                }
                            }
                        }
                    }
                    taskAnswerFile {
                        id
                        logoSrc
                        src
                        name
                    }
                    impactTrackerAnswerSet {
                        id
                        impactLikertAnswers {
                            id
                            statement
                            leftLabel
                            rightLabel
                            value
                        }
                    }
                    selectInImageMedia {
                        id
                        mediaType
                        src
                        name
                        logo
                    }
                    imageSelectionAnswers {
                        id
                        part
                        clickX
                        clickY
                        answerType
                        answer
                        explanation
                    }
                    imageSelectionScore {
                        earnedScore
                        possibleScore
                    }
                }
            }
        }
    }
`);

export type ISubmittedTask = NonNullable<ResultOf<typeof query>["submittedTask"]>;
export type IPerson = NonNullable<ISubmittedTask["feedbacker"]>;
export type ISharedAnswer = ISubmittedTask["sharedAnswers"][number];
export type ITaskAnswer = ISharedAnswer["taskAnswers"][number];
export type IQuizAnswerSet = NonNullable<ITaskAnswer["quizAnswerSet"]>;
export type IPollAnswerSet = NonNullable<ITaskAnswer["pollAnswerSet"]>;
export type ITaskAiThread = NonNullable<ISubmittedTask["submittedTaskVariant"]["taskAiThread"]>;
export type IFeedbackRequest = NonNullable<ISubmittedTask["submittedTaskVariant"]["feedbackRequest"]>;

// Mark read mutaion

export const markReadMutation = graphql(`
    mutation SubmittedTaskMarkReads($ids: [String!]!) {
        submittedTaskMarkReads(ids:$ids) {
            error        }
    }
`);
